import { supabase } from "~/integrations/supabase/client";
import { Database } from "~/types/supabase";

type TotemStatus = Database["public"]["Enums"]["totem_status"];
type Totem = Database["public"]["Tables"]["totems"]["Row"];
type TotemInsert = Database["public"]["Tables"]["totems"]["Insert"];
type TotemUpdate = Database["public"]["Tables"]["totems"]["Update"];
type TotemMovement = Database["public"]["Tables"]["totem_movements"]["Row"];
type TotemPending =
  Database["public"]["Tables"]["totem_pending_assignments"]["Row"];
type TotemPendingUpdate =
  Database["public"]["Tables"]["totem_pending_assignments"]["Update"];

export interface TotemWithRelations extends Totem {
  client?: {
    id: string;
    name: string;
  } | null;
  product?: {
    id: string;
    short_name: string;
  } | null;
}

export interface TotemFilters {
  status?: TotemStatus | TotemStatus[];
  clientId?: string;
  search?: string;
  includeDeleted?: boolean;
}

export interface TotemStats {
  available: number;
  filled: number;
  in_transit: number;
  at_client: number;
  pickup_ready: number;
  pickup_scheduled: number;
  pickup_transit: number;
  returned: number;
  cleaning: number;
  damaged: number;
  lost: number;
  total: number;
}

export interface TotemFleetStatusCount {
  status: TotemStatus;
  count: number;
}

// Fleet counts grouped by status via a SECURITY DEFINER RPC, so the Consulta de Stock
// detail view can show the returnable-totem breakdown even for roles without direct
// (RLS-restricted, logistica/superadmin-only) read access to the totems table.
export async function getReturnableTotemFleetSummary(): Promise<
  TotemFleetStatusCount[]
> {
  const { data, error } = await supabase.rpc(
    "get_returnable_totem_fleet_summary"
  );
  if (error) throw error;
  return (data ?? []).map((row) => ({
    status: row.status as TotemStatus,
    count: Number(row.count),
  }));
}

// Fetch all totems with optional filters
export async function fetchTotems(
  filters?: TotemFilters
): Promise<TotemWithRelations[]> {
  let query = supabase
    .from("totems")
    .select(
      `
      *,
      client:clients!current_client_id(id, name),
      product:products!current_product_id(id, short_name)
    `
    )
    .order("updated_at", { ascending: false });

  if (!filters?.includeDeleted) {
    query = query.is("deleted_at", null);
  }

  if (filters?.status) {
    if (Array.isArray(filters.status)) {
      query = query.in("status", filters.status);
    } else {
      query = query.eq("status", filters.status);
    }
  }

  if (filters?.clientId) {
    query = query.eq("current_client_id", filters.clientId);
  }

  if (filters?.search) {
    query = query.ilike("code", `%${filters.search}%`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data as TotemWithRelations[];
}

// Fetch single totem by ID
export async function fetchTotemById(
  id: string
): Promise<TotemWithRelations | null> {
  const { data, error } = await supabase
    .from("totems")
    .select(
      `
      *,
      client:clients!current_client_id(id, name),
      product:products!current_product_id(id, short_name)
    `
    )
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }
  return data as TotemWithRelations;
}

// Create a new totem
export async function createTotem(
  totem: Omit<TotemInsert, "id" | "created_at" | "updated_at">
): Promise<Totem> {
  const { data, error } = await supabase
    .from("totems")
    .insert(totem)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Update totem
export async function updateTotem(
  id: string,
  updates: TotemUpdate
): Promise<Totem> {
  const { data, error } = await supabase
    .from("totems")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Update totem status with movement logging
export async function updateTotemStatus(
  id: string,
  newStatus: TotemStatus,
  performedBy: string,
  options?: {
    notes?: string;
    clientId?: string;
    salesRequestId?: string;
    pickupOrderId?: string;
  }
): Promise<Totem> {
  // Get current totem
  const { data: currentTotem, error: fetchError } = await supabase
    .from("totems")
    .select("status")
    .eq("id", id)
    .single();

  if (fetchError) throw fetchError;

  // Update status
  const updates: TotemUpdate = { status: newStatus };

  // Clear client/product info when returning to available
  if (newStatus === "available") {
    updates.current_client_id = null;
    updates.current_product_id = null;
    updates.current_sales_request_id = null;
    updates.last_cleaned_at = new Date().toISOString();
    updates.total_cycles = (currentTotem as Totem).total_cycles
      ? (currentTotem as Totem).total_cycles + 1
      : 1;
  }

  // Set client if provided
  if (options?.clientId) {
    updates.current_client_id = options.clientId;
  }

  if (options?.salesRequestId) {
    updates.current_sales_request_id = options.salesRequestId;
  }

  const { data: updatedTotem, error: updateError } = await supabase
    .from("totems")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (updateError) throw updateError;

  // Log movement
  const { error: movementError } = await supabase
    .from("totem_movements")
    .insert({
      totem_id: id,
      from_status: currentTotem.status,
      to_status: newStatus,
      performed_by: performedBy,
      notes: options?.notes,
      client_id: options?.clientId,
      sales_request_id: options?.salesRequestId,
      pickup_order_id: options?.pickupOrderId,
    });

  if (movementError) throw movementError;

  return updatedTotem;
}

// Statuses where a totem is physically held by a client
export const AT_CLIENT_STATUSES: TotemStatus[] = [
  "at_client",
  "pickup_ready",
  "pickup_scheduled",
];

// Move up to `quantity` totems from one set of statuses ("bodega") to another,
// logging one movement per totem. Returns the number actually moved.
//
// Each totem is moved through `updateTotemStatus`, so the correct per-totem
// side-effects are always applied — most importantly, moving to "available"
// clears client/product/sales_request, stamps last_cleaned_at, and bumps
// total_cycles. Runs sequentially so a mid-batch failure leaves a clean prefix
// moved and returns the count actually moved.
export async function moveTotemsBetweenStatuses({
  fromStatuses,
  toStatus,
  quantity,
  performedBy,
  notes,
  clientId,
}: {
  fromStatuses: TotemStatus[];
  toStatus: TotemStatus;
  quantity: number;
  performedBy: string;
  notes?: string;
  clientId?: string;
}): Promise<number> {
  // Pick the oldest-updated matching totems (been in this state the longest).
  let query = supabase
    .from("totems")
    .select("id")
    .in("status", fromStatuses)
    .is("deleted_at", null)
    .order("updated_at", { ascending: true })
    .limit(quantity);

  // Scope to a specific client only for the "from a client" flow.
  if (clientId) {
    query = query.eq("current_client_id", clientId);
  }

  const { data: candidates, error: fetchError } = await query;

  if (fetchError) throw fetchError;
  if (!candidates || candidates.length === 0) return 0;

  let moved = 0;
  for (const totem of candidates) {
    // Pass `clientId` only for the from-client flow (stamps the movement's
    // client_id for provenance). For warehouse-to-warehouse moves it is
    // undefined, so updateTotemStatus never re-assigns a client.
    await updateTotemStatus(totem.id, toStatus, performedBy, {
      notes,
      clientId,
    });
    moved += 1;
  }

  return moved;
}

// Assign up to `quantity` warehouse totems (from `fromStatuses`, e.g. Disponible)
// to a client — moves each to `at_client` and sets `current_client_id`, logging one
// movement per totem. Unlike the from-client flow in moveTotemsBetweenStatuses, the
// source is NOT scoped to a client (warehouse totems don't belong to anyone yet).
// Used for the manual "mover a cliente" flow while reconciling; the factura flow
// (assignTotemsForFactura edge helper) assigns automatically going forward.
export async function assignTotemsToClient({
  fromStatuses,
  quantity,
  clientId,
  performedBy,
  notes,
}: {
  fromStatuses: TotemStatus[];
  quantity: number;
  clientId: string;
  performedBy: string;
  notes?: string;
}): Promise<number> {
  const { data: candidates, error: fetchError } = await supabase
    .from("totems")
    .select("id")
    .in("status", fromStatuses)
    .is("deleted_at", null)
    .order("updated_at", { ascending: true })
    .limit(quantity);

  if (fetchError) throw fetchError;
  if (!candidates || candidates.length === 0) return 0;

  let moved = 0;
  for (const totem of candidates) {
    await updateTotemStatus(totem.id, "at_client", performedBy, {
      clientId,
      notes,
    });
    moved += 1;
  }

  return moved;
}

// A movement row with its related totem, user, and client names resolved.
export interface TotemMovementWithRelations extends TotemMovement {
  totem?: { code: string } | null;
  performed_by_profile?: { name: string; lastname: string } | null;
  client?: { name: string } | null;
}

export interface TotemMovementDateFilters {
  from?: string; // ISO lower bound (inclusive)
  to?: string; // ISO upper bound (inclusive)
}

// Fetch totem movement history
export async function fetchTotemMovements(
  totemId: string
): Promise<TotemMovementWithRelations[]> {
  const { data, error } = await supabase
    .from("totem_movements")
    .select(
      `
      *,
      performed_by_profile:profiles!performed_by(name, lastname),
      client:clients!client_id(name)
    `
    )
    .eq("totem_id", totemId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as TotemMovementWithRelations[];
}

// Fetch every totem's movements (global history), newest first, optionally
// bounded by a created_at date range. Reads through the logistica/superadmin RLS
// on totem_movements (same access as the Totems page).
export async function fetchAllTotemMovements(
  filters?: TotemMovementDateFilters
): Promise<TotemMovementWithRelations[]> {
  let query = supabase
    .from("totem_movements")
    .select(
      `
      *,
      totem:totems!totem_id(code),
      performed_by_profile:profiles!performed_by(name, lastname),
      client:clients!client_id(name)
    `
    )
    .order("created_at", { ascending: false });

  if (filters?.from) query = query.gte("created_at", filters.from);
  if (filters?.to) query = query.lte("created_at", filters.to);

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as TotemMovementWithRelations[];
}

// Get totem statistics
export async function fetchTotemStats(): Promise<TotemStats> {
  const { data, error } = await supabase
    .from("totems")
    .select("status")
    .is("deleted_at", null);

  if (error) throw error;

  const stats: TotemStats = {
    available: 0,
    filled: 0,
    in_transit: 0,
    at_client: 0,
    pickup_ready: 0,
    pickup_scheduled: 0,
    pickup_transit: 0,
    returned: 0,
    cleaning: 0,
    damaged: 0,
    lost: 0,
    total: data.length,
  };

  data.forEach((totem) => {
    const status = totem.status as keyof Omit<TotemStats, "total">;
    if (status in stats) {
      stats[status]++;
    }
  });

  return stats;
}

// Soft delete totem
export async function deleteTotem(id: string): Promise<void> {
  const { error } = await supabase
    .from("totems")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", id);

  if (error) throw error;
}

// ============================================================================
// Pending assignments (shortfall alarm)
// ============================================================================
// When a factura with totem lines is emitted while the Disponible pool is empty
// or short, the assignTotemsForFactura edge helper records the faltante here (per
// sales_request + product). These rows surface in the Totems "Pendientes" tab so
// the shortfall isn't invisible. Reads/writes go through the same
// logistica/superadmin RLS as the other totem tables.

export interface TotemPendingWithRelations extends TotemPending {
  client?: { name: string } | null;
  product?: { short_name: string } | null;
  created_by_profile?: { name: string; lastname: string } | null;
}

// Fetch pending-assignment alarms, newest first. By default only unresolved
// (status = 'pending') rows; pass includeResolved to also load resolved history.
export async function fetchTotemPendingAssignments(opts?: {
  includeResolved?: boolean;
}): Promise<TotemPendingWithRelations[]> {
  let query = supabase
    .from("totem_pending_assignments")
    .select(
      `
      *,
      client:clients!client_id(name),
      product:products!product_id(short_name),
      created_by_profile:profiles!created_by(name, lastname)
    `
    )
    .order("created_at", { ascending: false });

  if (!opts?.includeResolved) {
    query = query.eq("status", "pending");
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as TotemPendingWithRelations[];
}

// Count of open (pending) shortfall alarms — feeds the tab badge, banner and stat card.
export async function fetchTotemPendingCount(): Promise<number> {
  const { count, error } = await supabase
    .from("totem_pending_assignments")
    .select("id", { count: "exact", head: true })
    .eq("status", "pending");
  if (error) throw error;
  return count ?? 0;
}

// Resolve a pending alarm. When `assignedNow` totems were just moved to the client
// the counts are decremented and the row stays 'pending' if a shortfall remains;
// otherwise (or on a manual dismissal with assignedNow = 0) it is marked 'resolved'.
export async function resolveTotemPending(
  id: string,
  performedBy: string,
  opts?: { assignedNow?: number; notes?: string }
): Promise<void> {
  const { data: row, error: fetchError } = await supabase
    .from("totem_pending_assignments")
    .select("assigned_count, pending_count")
    .eq("id", id)
    .single();
  if (fetchError) throw fetchError;

  const assignedNow = Math.max(0, opts?.assignedNow ?? 0);
  const newAssigned = row.assigned_count + assignedNow;
  const newPending = Math.max(0, row.pending_count - assignedNow);
  const nowIso = new Date().toISOString();

  // A manual resolve (assignedNow === 0) always closes the alarm; when totems were
  // moved, only close it once the shortfall is fully covered.
  const resolved = assignedNow === 0 || newPending === 0;

  const updates: TotemPendingUpdate = {
    assigned_count: newAssigned,
    pending_count: newPending,
    status: resolved ? "resolved" : "pending",
    resolved_at: resolved ? nowIso : null,
    resolved_by: resolved ? performedBy : null,
  };
  if (opts?.notes) updates.notes = opts.notes;

  const { error: updateError } = await supabase
    .from("totem_pending_assignments")
    .update(updates)
    .eq("id", id);
  if (updateError) throw updateError;
}

// Generate next totem code
export async function generateTotemCode(): Promise<string> {
  const { data, error } = await supabase
    .from("totems")
    .select("code")
    .order("created_at", { ascending: false })
    .limit(1);

  if (error) throw error;

  if (!data || data.length === 0) {
    return "TT-0001";
  }

  const lastCode = data[0].code;
  const match = lastCode.match(/TT-(\d+)/);
  if (match) {
    const nextNumber = parseInt(match[1], 10) + 1;
    return `TT-${nextNumber.toString().padStart(4, "0")}`;
  }

  return `TT-${Date.now()}`;
}
